import React from "react";
import { FiActivity, FiCheck, FiDownload, FiFileText, FiImage, FiLock, FiPrinter, FiUser } from "react-icons/fi";
import VitalsFields from "../../../../components/vitals/VitalsFields";
import VitalsReadonlyGrid from "../../../../components/vitals/VitalsReadonlyGrid";
import {
  DEFAULT_VITAL_PREFERENCES,
  hasAnyVitalPreferenceEnabled,
  hasRecordedVitals,
} from "../../../../utils/vitalPreferences";

const inputClass =
  "input min-h-[6.5rem] resize-y whitespace-pre-wrap break-words [overflow-wrap:anywhere]";
const noteClass =
  "text-sm text-slate-900 whitespace-pre-wrap break-words [overflow-wrap:anywhere]";

export default function DoctorSectionSimple(props) {
  const {
    isSoloDoctorClinic,
    latestVitals,
    vitalPreferences,
    statusSubmitting,
    handleDownloadReportPdf,
    handlePrintReportPdf,
    canCloseCase,
    onCloseCase,
    patientDisplayName,
    patientGender,
    patientPhone,
    formatDateDisplay,
    patientDob,
    canEditReportAndStatus,
    canReviseClosedCase = false,
    handleSubmitReport,
    report,
    setReport,
    reportSubmitting,
    medicalReport,
    doctorProfileLoading,
    signaturePreviewUrl,
    stampPreviewUrl,
    services,
    servicesLoading,
    selectedServiceId,
    setSelectedServiceId,
    serviceSubmitting,
    handleAttachService,
    attachedServiceName,
    attachedServicePrice,
    showRecordedVitals = true,
    canEditVitals = false,
    vitals,
    setVitals,
    onSaveVitals,
    vitalsSubmitting = false,
  } = props;

  const visitBusy = reportSubmitting || statusSubmitting || serviceSubmitting;
  const prefs = vitalPreferences || DEFAULT_VITAL_PREFERENCES;
  const showVitalsEditor = canEditVitals && hasAnyVitalPreferenceEnabled(prefs);
  const showVitalsRead = showRecordedVitals && !showVitalsEditor && hasRecordedVitals(latestVitals);
  const showVitalsBlock = showVitalsEditor || showVitalsRead;

  return (
    <div className="card overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
          <FiFileText className="text-slate-500" size={15} aria-hidden />
          Mjeku
        </h2>
      </div>

      {canEditVitals && !hasAnyVitalPreferenceEnabled(prefs) && (
        <div className="px-5 py-4 border-b border-slate-200">
          <p className="text-sm text-slate-600">
            Shenjat vitale janë të fikura. Aktivizojini te Preferencat në profilin e klinikës.
          </p>
        </div>
      )}

      {showVitalsBlock && (
        <div className="p-5 space-y-5">
          <section>
            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <FiActivity size={14} className="text-clinic-400" aria-hidden />
              Shenjat jetësore
            </h3>
            {showVitalsEditor ? (
              <form onSubmit={onSaveVitals} className="space-y-4">
                <VitalsFields vitals={vitals} setVitals={setVitals} vitalPreferences={prefs} />
                <button type="submit" disabled={vitalsSubmitting} className="btn-secondary btn-md">
                  {vitalsSubmitting ? "Duke ruajtur…" : "Ruaj shenjat"}
                </button>
              </form>
            ) : (
              <VitalsReadonlyGrid vitals={latestVitals} vitalPreferences={prefs} />
            )}
          </section>
        </div>
      )}

      <div className={showVitalsBlock ? "border-t border-slate-200" : ""}>
        <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-3 bg-slate-50/80 border-b border-slate-200">
          <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wide">
            Raporti mjekësor
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handlePrintReportPdf}
              className="btn-secondary btn-sm"
            >
              <FiPrinter size={16} aria-hidden />
              Printo
            </button>
            <button
              type="button"
              onClick={handleDownloadReportPdf}
              className="btn-secondary btn-sm"
            >
              <FiDownload size={16} aria-hidden />
              PDF
            </button>
            {canCloseCase && (
              <button
                type="button"
                disabled={statusSubmitting}
                onClick={onCloseCase}
                className="btn-primary btn-sm"
              >
                <FiLock size={16} aria-hidden />
                {statusSubmitting ? "Duke mbyllur…" : "Mbyll rastin"}
              </button>
            )}
          </div>
        </div>

        <div className="bg-white">
          <section className="border-b border-slate-200">
            <h4 className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
              <FiUser size={12} aria-hidden />
              Pacienti
            </h4>
            <div className="divide-y divide-slate-100">
              {[
                { label: "Emri", value: patientDisplayName },
                { label: "Gjinia", value: patientGender },
                { label: "Telefoni", value: patientPhone },
                { label: "Data e lindjes", value: formatDateDisplay(patientDob) },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="flex flex-wrap items-baseline justify-between gap-3 px-5 py-2.5 text-sm"
                >
                  <span className="text-slate-500">{label}</span>
                  <span className="font-medium text-slate-900 text-right">{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h4 className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
              <FiFileText size={12} aria-hidden />
              Shënime klinike
            </h4>
            {canEditReportAndStatus || canReviseClosedCase ? (
              <form onSubmit={handleSubmitReport} className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Anamneza</label>
                  <textarea
                    value={report.anamneza}
                    onChange={(e) => setReport((p) => ({ ...p, anamneza: e.target.value }))}
                    rows={3}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Ekzaminimi</label>
                  <textarea
                    value={report.ekzaminimi}
                    onChange={(e) => setReport((p) => ({ ...p, ekzaminimi: e.target.value }))}
                    rows={3}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Diagnoza *
                  </label>
                  <textarea
                    value={report.diagnosis}
                    onChange={(e) => setReport((p) => ({ ...p, diagnosis: e.target.value }))}
                    rows={3}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Terapia *
                  </label>
                  <textarea
                    value={report.therapy}
                    onChange={(e) => setReport((p) => ({ ...p, therapy: e.target.value }))}
                    rows={3}
                    required
                    className={inputClass}
                  />
                </div>
                {canEditReportAndStatus && (
                <div className="pt-4 border-t border-slate-100">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Shërbimi
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <select
                      value={selectedServiceId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedServiceId(value);
                        if (value) handleAttachService(value);
                      }}
                      disabled={servicesLoading || serviceSubmitting}
                      className="input h-10 min-h-0 py-1.5 sm:max-w-md"
                    >
                      <option value="">
                        {servicesLoading ? "Duke ngarkuar…" : "Zgjidh shërbimin"}
                      </option>
                      {services.map((s) => {
                        const sid = s.id ?? s.Id;
                        const name = s.name ?? s.Name ?? "—";
                        const price = s.price ?? s.Price;
                        return (
                          <option key={sid} value={sid}>
                            {name}{" "}
                            {typeof price === "number" ? `· ${price.toFixed(2)} EUR` : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {serviceSubmitting && (
                    <p className="text-xs text-slate-500 mt-2">Duke lidhur shërbimin…</p>
                  )}
                  {attachedServiceName && (
                    <p className="text-xs text-slate-500 mt-2">
                      <span className="font-medium text-slate-700">{attachedServiceName}</span>
                      {attachedServicePrice != null
                        ? ` · ${Number(attachedServicePrice).toFixed(2)} EUR`
                        : ""}
                    </p>
                  )}
                </div>
                )}
                {canReviseClosedCase && attachedServiceName && (
                  <p className="text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{attachedServiceName}</span>
                    {attachedServicePrice != null
                      ? ` · ${Number(attachedServicePrice).toFixed(2)} EUR`
                      : ""}
                  </p>
                )}
                <div className="flex flex-col items-start gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={visitBusy}
                    className="btn-primary btn-md"
                  >
                    {reportSubmitting || statusSubmitting ? (
                      <span className="animate-pulse">
                        {canReviseClosedCase ? "Duke ruajtur…" : "Duke përfunduar…"}
                      </span>
                    ) : canReviseClosedCase ? (
                      <>
                        <FiCheck size={16} /> Ruaj ndryshimet
                      </>
                    ) : (
                      <>
                        <FiCheck size={16} /> Përfundo vizitën
                      </>
                    )}
                  </button>
                  <p className="text-xs text-slate-500">
                    {canReviseClosedCase
                      ? "Rasti mbetet i mbyllur. Ruhet vetëm teksti i shënimeve klinike."
                      : isSoloDoctorClinic
                        ? "Diagnoza dhe terapia ruhen, pastaj rasti mbyllet dhe shfaqet te Raportet."
                        : "Diagnoza dhe terapia ruhen, pastaj rasti shfaqet te infermieri për mbyllje."}
                  </p>
                </div>
              </form>
            ) : (
              <div className="divide-y divide-slate-100">
                <div className="px-5 py-3 min-w-0">
                  <p className="text-xs text-slate-500 mb-1">Anamneza</p>
                  <p className={noteClass}>
                    {medicalReport?.anamneza ?? medicalReport?.Anamneza ?? "—"}
                  </p>
                </div>
                <div className="px-5 py-3 min-w-0">
                  <p className="text-xs text-slate-500 mb-1">Ekzaminimi</p>
                  <p className={noteClass}>
                    {medicalReport?.ekzaminimi ?? medicalReport?.Ekzaminimi ?? "—"}
                  </p>
                </div>
                <div className="px-5 py-3 min-w-0">
                  <p className="text-xs text-slate-500 mb-1">Diagnoza</p>
                  <p className={noteClass}>
                    {medicalReport?.diagnosis ?? medicalReport?.Diagnosis ?? "—"}
                  </p>
                </div>
                <div className="px-5 py-3 min-w-0">
                  <p className="text-xs text-slate-500 mb-1">Terapia</p>
                  <p className={noteClass}>
                    {medicalReport?.therapy ?? medicalReport?.Therapy ?? "—"}
                  </p>
                </div>
              </div>
            )}
          </section>

          <section className="border-t border-slate-200">
            <h4 className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
              <FiImage size={12} aria-hidden />
              Nënshkrimi dhe vula
            </h4>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                <p className="text-[10px] font-medium text-slate-500 uppercase mb-2">Nënshkrimi</p>
                {doctorProfileLoading ? (
                  <p className="text-sm text-slate-500 animate-pulse">…</p>
                ) : signaturePreviewUrl ? (
                  <img
                    src={signaturePreviewUrl}
                    alt="Nënshkrimi"
                    className="max-h-20 object-contain"
                  />
                ) : (
                  <p className="text-sm text-slate-500">—</p>
                )}
              </div>
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                <p className="text-[10px] font-medium text-slate-500 uppercase mb-2">Vula</p>
                {doctorProfileLoading ? (
                  <p className="text-sm text-slate-500 animate-pulse">…</p>
                ) : stampPreviewUrl ? (
                  <img src={stampPreviewUrl} alt="Vula" className="max-h-20 object-contain" />
                ) : (
                  <p className="text-sm text-slate-500">—</p>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
